/**
 * Enforces single-open accordion behavior for the PLP filters drawer
 * (`.facets--drawer .facets__filters-wrapper`). Horizon's native
 * accordion-custom.js has no "close others" logic and every filter group
 * ships with open-by-default-on-mobile/desktop, so all filters are always
 * expanded at once. This is scoped strictly to the drawer's filter list —
 * it never touches the horizontal filter bar or other theme accordions
 * (FAQ, PDP specs), which use the same accordion-custom component.
 */
const WRAPPER_SELECTOR = '.facets--drawer .facets__filters-wrapper';
const PANEL_SELECTOR = `${WRAPPER_SELECTOR} .facets__panel`;

// Remembers which panel index was open per wrapper, so a re-render (see
// below) restores the user's choice instead of always resetting to the
// first filter.
const openIndexByWrapper = new WeakMap();

function getPanels(wrapper) {
  return Array.from(wrapper.querySelectorAll('.facets__panel'));
}

function collapseToOpenIndex(wrapper) {
  const panels = getPanels(wrapper);
  const storedIndex = openIndexByWrapper.get(wrapper) ?? 0;
  const targetIndex = storedIndex < panels.length ? storedIndex : 0;

  panels.forEach((panel, index) => {
    panel.open = index === targetIndex;
  });
}

function closeSiblings(openedPanel) {
  const wrapper = openedPanel.closest(WRAPPER_SELECTOR);
  if (!wrapper) return;

  const panels = getPanels(wrapper);
  const openedIndex = panels.indexOf(openedPanel);
  if (openedIndex !== -1) openIndexByWrapper.set(wrapper, openedIndex);

  panels.forEach((panel) => {
    if (panel !== openedPanel) panel.open = false;
  });
}

// `toggle` on <details> doesn't bubble, so this must run in the capture phase.
document.addEventListener(
  'toggle',
  (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLDetailsElement)) return;
    if (!panel.matches(PANEL_SELECTOR)) return;
    if (!panel.open) return;

    closeSiblings(panel);
  },
  true
);

// The drawer content is patched in place by the Section Rendering API on
// every filter change (assets/facets.js), which re-applies the native
// open-by-default attributes to every group. Re-collapse to a single open
// panel whenever that markup gets patched, restoring the panel the user had
// open instead of always forcing the first one — otherwise applying a
// filter closes the very group the user just used.
const observer = new MutationObserver((mutations) => {
  const wrappers = new Set();

  mutations.forEach((mutation) => {
    const wrapper = mutation.target instanceof Element ? mutation.target.closest(WRAPPER_SELECTOR) : null;
    if (wrapper) wrappers.add(wrapper);
  });

  wrappers.forEach(collapseToOpenIndex);
});

function init() {
  document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
    collapseToOpenIndex(wrapper);
    observer.observe(wrapper, { childList: true, subtree: true });
  });
}

// accordion-custom's connectedCallback opens every filter group per its
// open-by-default-on-mobile/desktop attributes; wait for that upgrade to
// finish before collapsing, otherwise it can re-open everything right after.
if (customElements.get('accordion-custom')) {
  init();
} else {
  customElements.whenDefined('accordion-custom').then(init);
}
